import { defineStore } from 'pinia'

import EventBus, { GlobalEvents } from '@shared/event-bus'
import { StoreState } from '@shared/models/interfaces/store'
import { Attachment } from '@shared/models/proto/base/attachment'
import {
  IMessage,
  IQuickSignDocument,
} from '@shared/models/proto/base/messages'
import { resourcePath } from '@shared/plugins/api'
import { ApiTransport } from '@shared/plugins/api-transport'
import { updateState } from '@shared/utils/store/http-request-state'

import {
  GetFlattenedDocumentDTO,
  ShowQuickSignDTO,
  UpdateQuickSignDTO,
} from '@pc/models/proto/dto/quick-sign'
import {
  FlattenedDocumentRO,
  QuickSignItemRO,
} from '@pc/models/proto/ro/quick-sign'
import { useMessageAttachmentsStore } from '@pc/store/message-attachments'
import { useMessagesStore } from '@pc/store/messages'

interface State {
  quickSigns: Record<IMessage['id'], StoreState<IQuickSignDocument>>
}

export const apiTransport = new ApiTransport(resourcePath, 'pc')

const state = (): State => ({
  quickSigns: {},
})

export const useQuickSignStore = defineStore('quick-sign', {
  state,

  actions: {
    async updateQuickSignDocument(
      dto: {
        quickSignDocumentId: number
        annotation: string
        conversationId: number
      },
      messageId: IMessage['id']
    ) {
      const messagesStore = useMessagesStore()
      try {
        this.quickSigns[messageId] = updateState(this.quickSigns[messageId])

        const { data } = await apiTransport.put<
          UpdateQuickSignDTO,
          QuickSignItemRO
        >(
          `/quick_sign_documents/${dto.quickSignDocumentId}`,
          new UpdateQuickSignDTO(dto),
          UpdateQuickSignDTO,
          QuickSignItemRO,
          {
            onUploadProgress({ total, loaded }) {
              EventBus.emit(GlobalEvents.AsyncRequestProgress, {
                total,
                loaded,
              })
            },
          }
        )
        if (data) {
          this.quickSigns[messageId] = updateState(
            this.quickSigns[messageId],
            data
          )
          messagesStore.updateQuickSignDocumentInMessage(data)
        } else {
          delete this.quickSigns[messageId]
        }
        return data
      } catch (e) {
        this.quickSigns[messageId] = updateState(
          this.quickSigns[messageId],
          new Error(`${e}`)
        )
        throw new Error(`${e}`)
      }
    },

    async fetchQuickSign(
      quickSignDocumentId: number,
      messageId: IMessage['id']
    ) {
      const messagesStore = useMessagesStore()
      try {
        this.quickSigns[messageId] = updateState(this.quickSigns[messageId])

        const data = await apiTransport.get<ShowQuickSignDTO, QuickSignItemRO>(
          '/quick_sign_documents',
          new ShowQuickSignDTO({ quickSignDocumentId }),
          QuickSignItemRO
        )
        if (data.data) {
          this.quickSigns[messageId] = updateState(
            this.quickSigns[messageId],
            data.data
          )
        } else {
          delete this.quickSigns[messageId]
        }
        data?.data && messagesStore.updateQuickSignDocumentInMessage(data.data)
        return data
      } catch (e) {
        this.quickSigns[messageId] = updateState(
          this.quickSigns[messageId],
          new Error(`${e}`)
        )
        throw e
      }
    },

    async fetchFlattenDocument(
      quickSignDocumentId: number,
      messageId?: IMessage['id']
    ) {
      try {
        const { data } = await apiTransport.get<
          GetFlattenedDocumentDTO,
          FlattenedDocumentRO
        >(
          `/quick_sign_documents/${quickSignDocumentId}/get_flattened_document`,
          new GetFlattenedDocumentDTO({ quickSignDocumentId }),
          FlattenedDocumentRO
        )
        if (messageId && data?.url) {
          this.setFlattenUrlToAttachment(messageId, data.url)
        }
        return data?.url || ''
      } catch (e) {
        throw new Error(`${e}`)
      }
    },

    setFlattenUrlToAttachment(messageId: IMessage['id'], url: string) {
      const messageAttachmentsStore = useMessageAttachmentsStore()
      const attachment = messageAttachmentsStore.getAttachment(messageId)

      if (attachment) {
        if (attachment?.quickSignDocument) {
          messageAttachmentsStore.records[messageId] = updateState(
            messageAttachmentsStore.records[messageId],
            new Attachment({
              ...attachment,
              quickSignDocument: {
                ...(attachment?.quickSignDocument || {}),
                flattenedDocumentUrl: url,
              },
            })
          )
        }
      }
    },
  },

  getters: {
    getQuickSign: (state) => (messageId: IMessage['id']) =>
      state.quickSigns[messageId]?.data || null,
  },
})
